import React, { useState, useEffect, useRef } from 'react';
// @ts-ignore: CSS module declarations are handled by bundler/runtime
import './styles.css';
import { _internalDataStore, mobileAPI } from '../api/MobileAPI';
import { syncOnLogin } from '../services/initialSync';
import { useNotifications } from './hooks/useNotifications';
import { usePullToRefresh } from './hooks/usePullToRefresh';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { useAuthServiceBlocked, CLERK_LOAD_TIMEOUT_MS } from './hooks/useAuthServiceStatus';
import { isOnline } from '../api/connectivity';
import { useToast } from './hooks/useToast';
import { useAuth } from './hooks/useAuth';
import { useSwimmerData } from './hooks/useSwimmerData';
import { useCountyStandards } from './hooks/useCountyStandards';
import { SwimmerAPIProvider } from './context/SwimmerAPIContext';
import { applySwUpdate } from './swUpdateService';
import LoadingSpinner from './components/LoadingSpinner';
import Toast from './components/Toast';
import InstallPrompt from './components/InstallPrompt';
import NotificationBanner from './components/banners/NotificationBanner';
import BottomNav from './components/BottomNav';
import PullToRefreshIndicator from './components/PullToRefreshIndicator';
import SearchSection from './components/SearchSection';
import SwimmerComparison from './components/SwimmerComparison';
import SavedSwimmers from './components/SavedSwimmers';
import SwimmerDetails from './components/SwimmerDetails';
import ContactPage from './components/ContactPage';
import AboutPage from './components/AboutPage';
import StandardsViewer from './components/StandardsViewer';
import ClubLeaderboard from './components/ClubLeaderboard';
import NotificationsPage from './components/NotificationsPage';
import ImprovementsModal from './components/ImprovementsModal';
import ProfileOnboardingModal from './components/ProfileOnboardingModal';
import ProfileSettings from './components/ProfileSettings';
import GuestBanner from './components/banners/GuestBanner';
import LoginGate from './components/LoginGate';
import BootSplash from './components/BootSplash';
import OfflineBanner from './components/banners/OfflineBanner';
import UpdateBanner from './components/banners/UpdateBanner';
import AuthServiceBanner from './components/banners/AuthServiceBanner';
import BuyMeACoffeeButton from './components/BuyMeACoffeeButton';
const COMMON_STANDARD_COUNTRIES = ['England', 'Scotland', 'Wales', 'Northern Ireland', 'United Kingdom', 'International'];
const PTR_THRESHOLD = 80;
const RECONNECT_DEBOUNCE_MS = 1500;
const PROFILE_SKIP_KEY = 'swim-lab:profile-skip';
const App = () => {
    const { toast, showToast } = useToast();
    const auth = useAuth();
    const swimmer = useSwimmerData({ showToast, isAuthenticated: auth.isAuthenticated });
    const standards = useCountyStandards({ showToast });
    const [page, setPage] = useState('home');
    const [swimmerActiveTab, setSwimmerActiveTab] = useState('times');
    const [mobileView, setMobileView] = useState('search');
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [showProfileOnboarding, setShowProfileOnboarding] = useState(false);
    const swimmerDetailsRef = useRef(null);
    const comparisonRef = useRef(null);
    const online = useOnlineStatus();
    const authServiceBlocked = useAuthServiceBlocked(online);
    const wasOnline = useRef(online);
    const hasBooted = useRef(false);
    const [bootTimedOut, setBootTimedOut] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const isLoadedRef = useRef(auth.isLoaded);
    isLoadedRef.current = auth.isLoaded;
    const reloadTriggeredRef = useRef(false);
    const reconnectTimerRef = useRef(null);
    const { notifications, unreadCount, markRead, markAllRead, clearAll, deleteOne: deleteNotification, loading: notificationsLoading } = useNotifications(auth.isSignedIn);
    // Boot-splash deadline (incase of clerk service being blocked)
    // i.e corporate proxy issues
    useEffect(() => {
        if (auth.isLoaded)
            return;
        const id = window.setTimeout(() => setBootTimedOut(true), CLERK_LOAD_TIMEOUT_MS);
        return () => window.clearTimeout(id);
    }, [auth.isLoaded]);
    // Initial data load
    useEffect(() => {
        void swimmer.loadSavedSwimmers();
        void standards.loadCountyTimesStore();
        void standards.loadActiveStandards();
        void standards.loadSelectedStrokes();
        void standards.loadSelectedDistances();
    }, []);
    // Sync on sign-in, then reload data
    useEffect(() => {
        const account = auth.accounts[0];
        if (!account?.localAccountId)
            return;
        syncOnLogin(account.localAccountId, _internalDataStore)
            .then(() => {
            void swimmer.loadSavedSwimmers();
            void standards.loadCountyTimesStore();
            void standards.loadActiveStandards();
        })
            .catch((err) => console.warn('initial sync failed:', err));
    }, [auth.accounts[0]?.localAccountId]);
    // Prompt once per sign-in for Home Club / Role, skipping if the user already has them saved
    useEffect(() => {
        const account = auth.accounts[0];
        if (!account?.localAccountId)
            return;
        if (sessionStorage.getItem(PROFILE_SKIP_KEY) === 'true')
            return;
        mobileAPI.getProfile()
            .then(profile => {
            if (!profile.homeClub && !profile.role) {
                setShowProfileOnboarding(true);
            }
        })
            .catch(err => console.warn('[profile] failed to load profile:', err));
    }, [auth.accounts[0]?.localAccountId]);
    // Reload on coming back online
    useEffect(() => {
        if (!wasOnline.current && online) {
            // Clerk only attempts its inital auth check ince, at mount. If that attempt
            // failed while offline it never retries on its own, so the app would otherwise
            // stay on the local owner-id fallback forever - force a full reload so Clerk
            // gets a fresh singleton and can actually re-authenticate now the network is up.
            // Debounce this behind a stable-connection window (rather than reloading on the first blip)
            // so a flaky reconnect cant send us into a reload loop, and guard
            // with reloadTriggeredRef so we only ever do this once per session.
            if (!isLoadedRef.current && !reloadTriggeredRef.current) {
                setReconnecting(true);
                reconnectTimerRef.current = window.setTimeout(() => {
                    if (isOnline() && !isLoadedRef.current && !reloadTriggeredRef.current) {
                        reloadTriggeredRef.current = true;
                        window.location.reload();
                    }
                    else {
                        setReconnecting(false);
                    }
                }, RECONNECT_DEBOUNCE_MS);
            }
            else {
                void swimmer.loadSavedSwimmers();
                void standards.loadCountyTimesStore();
                void standards.loadActiveStandards();
            }
        }
        wasOnline.current = online;
        return () => {
            if (reconnectTimerRef.current !== null) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
                setReconnecting(false);
            }
        };
    }, [online]);
    const hasComparison = swimmer.comparisonSwimmers.length > 0;
    const activeDetailsRef = hasComparison ? comparisonRef : swimmerDetailsRef;
    const comparisonKey = swimmer.comparisonSwimmers.map(s => s.tiref).join(',');
    // Scroll to swimmer details (or comparison view) when it's opened.
    // On desktop the detail column sits beside the search/saved sidebar rather than below it
    // so scrolling the detail element into view instead scrolls the header off-screen; just
    // scroll the page to the top so the header and both columns stay visible.
    useEffect(() => {
        if (!swimmer.currentSwimmerData && !hasComparison)
            return;
        const id = requestAnimationFrame(() => {
            if (window.matchMedia('(min-width: 900px)').matches) {
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
            else {
                activeDetailsRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
            }
        });
        return () => cancelAnimationFrame(id);
    }, [swimmer.currentSwimmerData?.tiref, comparisonKey]);
    // Rotating the device changes the document height (mobile stacks sections vertically,
    // landscape can be short enough that the old scrollY no longer exists), so the browser
    // clamps scroll back to the top. Re-focus the open swimmer details/comparison view after the layout settles.
    useEffect(() => {
        if (!swimmer.currentSwimmerData && !hasComparison)
            return;
        let timeoutId;
        const refocus = () => {
            window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
                if (window.matchMedia('(min-width: 900px)').matches)
                    return;
                activeDetailsRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
            }, 150);
        };
        window.addEventListener('orientationchange', refocus);
        return () => {
            window.removeEventListener('orientationchange', refocus);
            window.clearTimeout(timeoutId);
        };
    }, [swimmer.currentSwimmerData?.tiref, comparisonKey]);
    // Push notifications deep-link with ?tiref= - send user to the Alerts page rather than the swimmer directly
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('tiref'))
            return;
        window.history.replaceState({}, '', window.location.pathname);
        setPage('notifications');
    }, []);
    useEffect(() => {
        const handler = () => setUpdateAvailable(true);
        window.addEventListener('sw-update-available', handler);
        return () => window.removeEventListener('sw-update-available', handler);
    }, []);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const pendingToast = window.sessionStorage.getItem('swim-lab:pending-toast');
        if (!pendingToast)
            return;
        try {
            const parsed = JSON.parse(pendingToast);
            if (parsed.message) {
                showToast(parsed.message, parsed.type ?? 'success');
            }
        }
        catch {
            showToast('Signed out', 'success');
        }
        finally {
            window.sessionStorage.removeItem('swim-lab:pending-toast');
        }
    }, [showToast]);
    const handleApplyUpdate = () => {
        setUpdateAvailable(false);
        applySwUpdate();
    };
    const handleSaveProfile = async (profile) => {
        try {
            await mobileAPI.saveProfile(profile);
            setShowProfileOnboarding(false);
            showToast('Profile saved', 'success');
        }
        catch (err) {
            console.error('[profile] failed to save profile:', err);
            showToast('Failed to save profile', 'error');
        }
    };
    const handleSkipProfile = () => {
        sessionStorage.setItem(PROFILE_SKIP_KEY, 'true');
        setShowProfileOnboarding(false);
    };
    const { pullDistance, refreshing: ptrRefreshing } = usePullToRefresh({
        threshold: PTR_THRESHOLD,
        enabled: page === 'home',
        onRefresh: async () => {
            if (swimmer.currentSwimmerData) {
                await swimmer.handleRefreshCurrentSwimmer();
            }
            else {
                await swimmer.loadSavedSwimmers();
                showToast('Refreshed');
            }
        },
    });
    const detailOpen = !!swimmer.currentSwimmerData || swimmer.comparisonSwimmers.length > 0;
    if (reconnecting || (!hasBooted.current && !auth.isLoaded && online && !bootTimedOut)) {
        return React.createElement(BootSplash, null);
    }
    hasBooted.current = true;
    return (React.createElement(SwimmerAPIProvider, null,
        React.createElement(React.Fragment, null,
            React.createElement(PullToRefreshIndicator, { pullDistance: pullDistance, refreshing: ptrRefreshing, threshold: PTR_THRESHOLD }),
            React.createElement("header", { ref: auth.navRef },
                React.createElement("div", { className: "header-content" },
                    React.createElement("a", { href: "/", className: "logo" },
                        React.createElement("div", { className: "header-icon" },
                            React.createElement("svg", { width: "36", height: "36", viewBox: "0 0 122.88 82.13", xmlns: "http://www.w3.org/2000/svg" },
                                React.createElement("g", { fill: "currentColor" },
                                    React.createElement("path", { d: "M0,66.24c7.11-2.74,13.1-0.95,21.42,1.55c2.17,0.65,4.53,1.36,6.66,1.92c1.9,0.5,4.82-0.58,7.88-1.71 c3.82-1.41,7.8-2.87,12.57-2.75c3.6,0.09,6.63,1.74,9.69,3.41c1.92,1.05,3.87,2.11,4.95,2.15c1.24,0.04,3.08-1.04,4.92-2.12 c3-1.77,6-3.54,10.17-3.68c4.48-0.15,7.95,1.39,11.39,2.92c1.96,0.87,3.91,1.74,5.54,1.86c1.54,0.12,3.6-1.2,5.6-2.47 c2.78-1.78,5.51-3.52,9.1-3.92c4.27-0.47,8.93,1.54,12.89,3.24l0.1,0.05c0,4.05,0,8.11,0,12.16c-0.85-0.25-1.73-0.59-2.64-0.96 c-0.63-0.26-1.28-0.54-1.94-0.82c-2.71-1.16-5.9-2.54-7.17-2.4c-1.02,0.11-2.63,1.14-4.27,2.19c-0.6,0.38-1.21,0.77-1.82,1.15 c-3.04,1.85-6.34,3.43-10.69,3.1c-3.54-0.27-6.42-1.55-9.31-2.84l-0.25-0.11c-2.16-0.96-4.33-1.89-6.17-1.83 c-1.13,0.04-2.75,0.95-4.39,1.91l-0.38,0.22c-3.25,1.92-6.51,3.84-11.08,3.67c-3.73-0.14-6.87-1.84-9.96-3.53l-0.39-0.21 c-1.72-0.94-3.37-1.8-4.16-1.82c-2.42-0.06-5.21,0.91-7.92,1.91l-0.47,0.17c-4.74,1.75-9.26,3.41-14.62,2.01 c-2.88-0.75-5.06-1.41-7.06-2.01l-0.06-0.02c-7.25-2.18-11.98-3.58-17.65,0.13c-0.15,0.1-0.31,0.2-0.47,0.31v-0.31V66.24L0,66.24z M87.91,17.06l14.16-2.15c8.81-1.32,6.16-17.18-5.13-14.64l-32.11,5.3c-3.48,0.57-9.45,1.01-12.05,3.33 c-1.49,1.33-2.11,3.18-1.77,5.49c0.48,3.27,3.21,7.37,4.85,10.34l3.97,7.14c2.89,5.19,4.44,5.69-0.91,8.56L22.45,59.99l2.67,0.79 l8.01,0.12c0.91-0.3,1.86-0.65,2.83-1.01c3.82-1.41,7.8-2.87,12.57-2.75c3.6,0.09,6.63,1.74,9.69,3.41l1.38,0.74l7.06,0.11 c0.47-0.26,0.95-0.54,1.42-0.82c3-1.77,6-3.54,10.17-3.68c4.48-0.15,7.95,1.39,11.39,2.92c1.96,0.87,3.91,1.74,5.54,1.86 c0.37,0.03,0.77-0.03,1.19-0.14L77.79,28.5c-1.58-2.81-4.42-6.36-4.01-8.5c0.14-0.72,1.1-1.01,2.27-1.19 C80.01,18.24,83.95,17.66,87.91,17.06L87.91,17.06z M103.21,24.42c7.77,0,14.07,6.3,14.07,14.07c0,7.77-6.3,14.07-14.07,14.07 c-7.77,0-14.07-6.3-14.07-14.07C89.15,30.71,95.44,24.42,103.21,24.42L103.21,24.42z" }))))),
                    React.createElement("h1", null, "Swim Lab"),
                    React.createElement("span", { className: "header-theme" },
                        auth.accounts.length > 0 && (React.createElement("div", { className: "header-user" }, auth.accounts[0].name)),
                        React.createElement(ProfileSettings, { unreadCount: unreadCount, onOpenNotifications: () => setPage('notifications'), onOpenContact: () => setPage('contact'), onOpenAbout: () => setPage('about'), onShowToast: showToast }),
                        React.createElement(BuyMeACoffeeButton, null)),
                    React.createElement("button", { className: "nav-toggle", onClick: () => auth.setNavOpen(o => !o), "aria-label": "Toggle menu", "aria-expanded": auth.navOpen },
                        React.createElement("span", null),
                        React.createElement("span", null),
                        React.createElement("span", null))),
                auth.navOpen && (React.createElement("nav", { className: "nav-drawer" },
                    React.createElement("div", { className: "nav-drawer-content" }, ['about', 'contact', 'standards', 'clubs'].map(p => (React.createElement("strong", { key: p },
                        React.createElement("button", { className: "btn-ghost", style: { color: 'var(--primary)' }, onClick: () => { setPage(p); auth.setNavOpen(false); } }, p.charAt(0).toUpperCase() + p.slice(1))))))))),
            React.createElement("div", { className: "container" },
                React.createElement("main", { className: page === 'home' && detailOpen ? 'main-detail-open' : '' },
                    updateAvailable && React.createElement(UpdateBanner, { onUpdate: handleApplyUpdate }),
                    !online && React.createElement(OfflineBanner, null),
                    authServiceBlocked && React.createElement(AuthServiceBanner, null),
                    page === 'home' ? (!auth.isAuthenticated && !auth.guestMode && online ? (React.createElement(LoginGate, { onContinueAsGuest: auth.enterGuestMode })) : (React.createElement(React.Fragment, null,
                        !auth.isAuthenticated && React.createElement(GuestBanner, null),
                        React.createElement(NotificationBanner, null),
                        React.createElement("div", { className: "sidebar-column" },
                            React.createElement("div", { className: `mobile-view mobile-view-search${mobileView === 'search' ? ' mobile-view-active' : ''}${detailOpen ? ' mobile-detail-active' : ''}` },
                                React.createElement(SearchSection, { onSwimmerSelect: swimmer.handleSearchResults, onSaveSwimmer: swimmer.handleSaveFromSearch, savedTirefs: swimmer.savedTirefs, loading: swimmer.loading, setLoading: swimmer.setLoading, showToast: showToast })),
                            React.createElement("div", { className: `mobile-view mobile-view-saved${mobileView === 'saved' ? ' mobile-view-active' : ''}${detailOpen ? ' mobile-detail-active' : ''}` },
                                React.createElement(SavedSwimmers, { swimmers: swimmer.savedSwimmers, onLoad: swimmer.handleLoadSavedSwimmer, onDelete: swimmer.handleDeleteSwimmer, onRefreshAll: swimmer.handleRefreshAllSwimmers, onCompare: swimmer.handleCompareSwimmers, onUpdateTags: swimmer.handleUpdateSwimmerTags, onToggleNotifications: swimmer.handleUpdateSwimmerNotifications }))),
                        detailOpen && (React.createElement("div", { className: "detail-column" },
                            swimmer.comparisonSwimmers.length > 0 && (React.createElement("div", { ref: comparisonRef, className: "swimmer-comparison-anchor" },
                                React.createElement(SwimmerComparison, { swimmers: swimmer.comparisonSwimmers, onClose: swimmer.handleCloseComparison, selectedStrokes: standards.selectedStrokes, onSelectedStrokesChange: standards.handleSelectedStrokesChange, selectedDistances: standards.selectedDistances, onSelectedDistancesChange: standards.handleSelectedDistancesChange }))),
                            swimmer.currentSwimmerData && (React.createElement("div", { ref: swimmerDetailsRef, className: "swimmer-details-anchor" },
                                React.createElement(SwimmerDetails, { swimmerData: swimmer.currentSwimmerData, countyTimesStore: standards.countyTimesStore, activeStandards: standards.activeStandards, onActiveStandardsChange: standards.handleActiveStandardsChange, selectedStrokes: standards.selectedStrokes, onSelectedStrokesChange: standards.handleSelectedStrokesChange, selectedDistances: standards.selectedDistances, onSelectedDistancesChange: standards.handleSelectedDistancesChange, activeTab: swimmerActiveTab, onActiveTabChange: setSwimmerActiveTab, onSave: swimmer.handleSaveSwimmer, isSaved: swimmer.isCurrentSwimmerSaved, onExport: swimmer.handleExportToExcel, onClear: swimmer.handleClearDetails, onRefresh: swimmer.handleRefreshCurrentSwimmer, defaultStandardNames: standards.defaultStandardNames, onLoadCountyTimes: standards.pickAndLoadCountyTimes, onClearOneCounty: standards.clearOneCounty, onRegionChange: swimmer.handleUpdateSwimmerRegion, onCountyChange: swimmer.handleUpdateSwimmerCounty, onCountySelected: swimmer.handleCountySelected, onComparisonChange: swimmer.setComparisonResult, onCombinedAgeGroupChange: swimmer.handleUpdateCombinedAgeGroup, onSwimmerDataChange: swimmer.setCurrentSwimmerData, onRankingsSaved: swimmer.loadSavedSwimmers, loading: swimmer.loading, setLoading: swimmer.setLoading, showToast: showToast })))))))) : page === 'notifications' ? (React.createElement(NotificationsPage, { onBack: () => setPage('home'), notifications: notifications, onMarkRead: markRead, onMarkAllRead: markAllRead, onClearAll: clearAll, onDelete: deleteNotification, loading: notificationsLoading, onSwimmerClick: (tiref) => {
                            const linked = swimmer.savedSwimmers.find(s => s.tiref === tiref);
                            if (linked) {
                                swimmer.handleLoadSavedSwimmer(linked);
                                setMobileView('saved');
                            }
                            setPage('home');
                        } })) : page === 'standards' ? (React.createElement(StandardsViewer, { countyTimesStore: standards.countyTimesStore, defaultStandardNames: standards.defaultStandardNames, onClearOneCounty: standards.clearOneCounty, onBack: () => setPage('home'), onLoadCountyTimes: standards.pickAndLoadCountyTimes })) : page === 'clubs' ? (swimmer.savedSwimmers.length > 0 ? (React.createElement(ClubLeaderboard, { swimmerData: swimmer.savedSwimmers[0], loading: false, setLoading: swimmer.setLoading, showToast: showToast })) : (React.createElement("div", { className: "empty-state" },
                        React.createElement("p", null, "Save a swimmer first to user the Club Leaderboard - it uses the swimmer's events to build the comparison.")))) : page === 'about' ? (React.createElement(AboutPage, { onBack: () => setPage('home') })) : (React.createElement(ContactPage, { onBack: () => setPage('home'), showToast: showToast }))),
                standards.pendingLoad && (React.createElement("div", { className: "modal-backdrop", onClick: () => standards.setPendingLoad(null) },
                    React.createElement("div", { className: "modal", onClick: e => e.stopPropagation() },
                        React.createElement("div", { className: "modal-header" },
                            React.createElement("h3", null, "Load Standards"),
                            React.createElement("button", { className: "modal-close", onClick: () => standards.setPendingLoad(null), "aria-label": "Cancel" }, "\u00D7")),
                        React.createElement("div", { className: "modal-body" },
                            React.createElement("p", { className: "pending-load-note" }, "Saved as \"[Name] [Year]\". Edit the name or year to distinguish multiple versions for the same standard."),
                            React.createElement("datalist", { id: "standard-country-options" }, COMMON_STANDARD_COUNTRIES.map(country => (React.createElement("option", { key: country, value: country })))),
                            standards.pendingLoad.map((item, i) => (React.createElement("div", { key: i, className: "pending-load-item" },
                                (standards.pendingLoad.length > 1 || item.fileName) && (React.createElement("div", { className: "pending-load-item-header" },
                                    standards.pendingLoad.length > 1 && React.createElement("span", { className: "pending-load-item-number" },
                                        "#",
                                        i + 1),
                                    item.fileName && React.createElement("span", { className: "pending-load-source" }, item.fileName))),
                                React.createElement("div", { className: "pending-load-name-row" },
                                    React.createElement("label", { className: "pending-load-field" },
                                        React.createElement("span", null, "Standard Name"),
                                        React.createElement("input", { type: "text", value: item.standardName, onChange: e => standards.setPendingLoad(prev => prev.map((it, j) => j === i ? { ...it, standardName: e.target.value } : it)), placeholder: item.countyName })),
                                    React.createElement("label", { className: "pending-load-field" },
                                        React.createElement("span", null, "Year"),
                                        React.createElement("input", { type: "number", value: item.year, onChange: e => standards.setPendingLoad(prev => prev.map((it, j) => j === i ? { ...it, year: parseInt(e.target.value) || it.year } : it)), placeholder: "Year" }))),
                                React.createElement("label", { className: "pending-load-field" },
                                    React.createElement("span", null, "Country"),
                                    React.createElement("input", { type: "text", list: "standard-country-options", value: item.country, onChange: e => standards.setPendingLoad(prev => prev.map((it, j) => j === i ? { ...it, country: e.target.value } : it)), placeholder: "Country" })))))),
                        React.createElement("div", { className: "modal-actions" },
                            React.createElement("button", { type: "button", className: "btn", style: { opacity: 0.8 }, onClick: () => standards.setPendingLoad(null) }, "Cancel"),
                            React.createElement("button", { type: "button", className: "btn", onClick: standards.confirmLoad }, "Load"))))),
                swimmer.improvementsModal && (React.createElement(ImprovementsModal, { swimmers: swimmer.improvementsModal, onClose: () => swimmer.setImprovementsModal(null) })),
                showProfileOnboarding && (React.createElement(ProfileOnboardingModal, { onSave: handleSaveProfile, onSkip: handleSkipProfile })),
                swimmer.loading && React.createElement(LoadingSpinner, null),
                toast && React.createElement(Toast, { message: toast.message, type: toast.type }),
                React.createElement(InstallPrompt, null)),
            React.createElement(BottomNav, { swimmerLoaded: !!swimmer.currentSwimmerData, comparisonActive: swimmer.comparisonSwimmers.length > 0, page: page, activeTab: swimmerActiveTab, onActiveTabChange: setSwimmerActiveTab, onCloseSwimmer: swimmer.handleClearDetails, onCloseComparison: swimmer.handleCloseComparison, mobileView: mobileView, onJumpToSearch: () => { setPage('home'); setMobileView('search'); }, onJumpToSaved: () => { setPage('home'); setMobileView('saved'); }, onJumpToStandards: () => setPage('standards'), onJumpToClubs: () => setPage('clubs'), onJumpToNotifications: () => setPage('notifications'), unreadCount: unreadCount, onAuthClick: auth.handleAuthClick, isSignedIn: auth.isSignedIn }))));
};
export default App;
